import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import {
  LineChart,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
  CartesianGrid,
} from "recharts";
import { QrCode, Smartphone, Monitor, Tablet, Bot, Globe, MousePointerClick, CalendarClock, Loader2 } from "lucide-react";
import api from "@/lib/api";

function DeviceIcon({ device }) {
  const cls = "w-4 h-4";
  if (device === "mobile") return <Smartphone className={cls} />;
  if (device === "tablet") return <Tablet className={cls} />;
  if (device === "desktop") return <Monitor className={cls} />;
  if (device === "bot") return <Bot className={cls} />;
  return <Globe className={cls} />;
}

export default function PublicShare() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [timeline, setTimeline] = useState([]);
  const [scans, setScans] = useState([]);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [{ data: overview }, { data: tl }, { data: sc }] = await Promise.all([
          api.get(`/public/campaigns/${token}`),
          api.get(`/public/campaigns/${token}/timeline?days=30`),
          api.get(`/public/campaigns/${token}/scans?limit=100`),
        ]);
        setData(overview);
        setScans(sc);
        setTimeline(
          tl.series.map((d) => ({
            ...d,
            label: new Date(d.date).toLocaleDateString(undefined, { month: "short", day: "numeric" }),
          }))
        );
      } catch (e) {
        setErr("This share link is invalid or has been disabled.");
      } finally {
        setLoading(false);
      }
    })();
  }, [token]);

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
      </div>
    );
  }

  if (err || !data) {
    return (
      <div className="min-h-screen bg-slate-50 flex flex-col">
        <div className="max-w-md mx-auto flex-1 flex flex-col justify-center px-6 py-12 text-center">
          <div className="text-6xl">🔒</div>
          <h1 className="mt-4 text-2xl font-bold text-slate-900">Share link unavailable</h1>
          <p className="mt-2 text-sm text-slate-500">{err || "The owner may have disabled sharing for this campaign."}</p>
          <Link to="/" className="mt-6 text-sm font-semibold text-blue-600 hover:text-blue-700">
            Back to Qdurator →
          </Link>
        </div>
      </div>
    );
  }

  const totalDevices = data.devices.reduce((s, d) => s + d.count, 0) || 1;

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Slim header (no auth) */}
      <header className="border-b border-slate-200 bg-white/85 backdrop-blur-xl">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 h-14 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="public-navbar-logo">
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-blue-600 to-cyan-500 flex items-center justify-center">
              <QrCode className="w-4 h-4 text-white" strokeWidth={2.5} />
            </div>
            <span className="font-black tracking-tight text-slate-900">Qdurator</span>
          </Link>
          <Link
            to="/"
            className="text-xs font-semibold text-slate-500 hover:text-slate-900"
          >
            Public stats — read only
          </Link>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        {/* Hero */}
        <div className="text-center">
          <span className="text-xs font-bold uppercase tracking-[0.15em] text-blue-600" data-testid="public-eyebrow">
            Campaign performance
          </span>
          <h1
            data-testid="public-campaign-name"
            className="mt-3 text-3xl sm:text-5xl font-black tracking-tight text-slate-900"
          >
            {data.campaign_name}
          </h1>
          <p className="mt-3 text-sm text-slate-500">
            Shared by <span className="font-semibold text-slate-700">{data.workspace_name}</span>
            {data.folder ? <> · <span className="font-mono text-slate-600">{data.folder}</span></> : null}
          </p>
        </div>

        {/* KPIs */}
        <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-5" data-testid="public-kpis">
          <div className="bg-white border border-slate-200 rounded-2xl p-6">
            <div className="flex items-start justify-between">
              <div>
                <div className="text-xs font-bold uppercase tracking-[0.15em] text-slate-500">
                  Total scans
                </div>
                <div className="mt-3 text-4xl font-black tracking-tight font-mono text-slate-900 tabular-nums" data-testid="public-total-scans">
                  {data.total_scans.toLocaleString()}
                </div>
              </div>
              <div className="w-11 h-11 rounded-xl bg-cyan-50 text-cyan-600 flex items-center justify-center">
                <MousePointerClick className="w-5 h-5" strokeWidth={2.2} />
              </div>
            </div>
          </div>
          <div className="bg-white border border-slate-200 rounded-2xl p-6">
            <div className="flex items-start justify-between">
              <div>
                <div className="text-xs font-bold uppercase tracking-[0.15em] text-slate-500">
                  This week
                </div>
                <div className="mt-3 text-4xl font-black tracking-tight font-mono text-slate-900 tabular-nums" data-testid="public-week-scans">
                  {data.scans_this_week.toLocaleString()}
                </div>
              </div>
              <div className="w-11 h-11 rounded-xl bg-violet-50 text-violet-600 flex items-center justify-center">
                <CalendarClock className="w-5 h-5" strokeWidth={2.2} />
              </div>
            </div>
          </div>
        </div>

        {/* Timeline */}
        <div className="mt-6 bg-white border border-slate-200 rounded-2xl p-6" data-testid="public-timeline-chart">
          <div className="flex items-baseline justify-between mb-4">
            <div>
              <h2 className="text-lg font-bold text-slate-900">Last 30 days</h2>
              <p className="text-xs text-slate-500 mt-0.5">Daily scan volume</p>
            </div>
          </div>
          <div className="h-56">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={timeline} margin={{ left: -20, right: 8, top: 8, bottom: 0 }}>
                <defs>
                  <linearGradient id="pubGrad" x1="0" y1="0" x2="1" y2="0">
                    <stop offset="0%" stopColor="#2563EB" />
                    <stop offset="100%" stopColor="#06B6D4" />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" vertical={false} />
                <XAxis dataKey="label" stroke="#94A3B8" fontSize={11} tickLine={false} axisLine={false} interval="preserveStartEnd" />
                <YAxis stroke="#94A3B8" fontSize={11} tickLine={false} axisLine={false} allowDecimals={false} />
                <Tooltip contentStyle={{ borderRadius: 12, border: "1px solid #E2E8F0", fontSize: 12 }} />
                <Line type="monotone" dataKey="scans" stroke="url(#pubGrad)" strokeWidth={3} dot={{ r: 3, fill: "#2563EB" }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Device mix */}
        {data.devices.length > 0 && (
          <div className="mt-6 bg-white border border-slate-200 rounded-2xl p-6" data-testid="public-devices-card">
            <h2 className="text-lg font-bold text-slate-900 mb-4">Devices (last 30 days)</h2>
            <ul className="space-y-3">
              {data.devices.map((d) => {
                const pct = Math.round((d.count / totalDevices) * 100);
                return (
                  <li key={d.device} className="flex items-center gap-3" data-testid={`public-device-${d.device}`}>
                    <div className="w-8 h-8 rounded-lg bg-slate-100 text-slate-600 flex items-center justify-center">
                      <DeviceIcon device={d.device} />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center justify-between text-sm mb-1">
                        <span className="font-semibold capitalize text-slate-900">{d.device}</span>
                        <span className="font-mono text-slate-500">
                          <b className="text-slate-900">{d.count}</b> · {pct}%
                        </span>
                      </div>
                      <div className="h-1.5 rounded-full bg-slate-100 overflow-hidden">
                        <div
                          className="h-full bg-gradient-to-r from-blue-600 to-cyan-500"
                          style={{ width: `${pct}%` }}
                        />
                      </div>
                    </div>
                  </li>
                );
              })}
            </ul>
          </div>
        )}

        {/* Scan history (sanitized: no IPs, no user-agents) */}
        <div className="mt-6 bg-white border border-slate-200 rounded-2xl overflow-hidden" data-testid="public-scan-history">
          <div className="flex flex-wrap items-baseline justify-between gap-3 px-6 py-4 border-b border-slate-100">
            <div>
              <h2 className="text-lg font-bold text-slate-900">Recent scans</h2>
              <p className="text-xs text-slate-500 mt-0.5">
                Latest {Math.min(scans.length, 100)} events · timestamps and locations only (no personal data)
              </p>
            </div>
            <span className="text-xs font-semibold uppercase tracking-wider text-slate-400">
              {scans.length} shown
            </span>
          </div>
          {scans.length === 0 ? (
            <div className="px-6 py-14 text-center text-sm text-slate-500">
              No scans yet. Once someone scans the QR, it'll show up here.
            </div>
          ) : (
            <div className="w-full overflow-x-auto">
              <table className="w-full" data-testid="public-scan-table">
                <thead>
                  <tr className="bg-slate-50 text-xs font-bold uppercase tracking-wider text-slate-500 border-b border-slate-100">
                    <th className="px-6 py-3 text-left">When</th>
                    <th className="px-6 py-3 text-left">Device</th>
                    <th className="px-6 py-3 text-left">Location</th>
                  </tr>
                </thead>
                <tbody>
                  {scans.map((s, i) => (
                    <tr
                      key={`${s.timestamp}-${i}`}
                      data-testid={`public-scan-row-${i}`}
                      className="border-b border-slate-100 last:border-0 hover:bg-slate-50/60"
                    >
                      <td className="px-6 py-3 text-sm text-slate-700 whitespace-nowrap font-mono">
                        {new Date(s.timestamp).toLocaleString()}
                      </td>
                      <td className="px-6 py-3 text-sm">
                        <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md bg-slate-100 text-slate-700 font-medium text-xs capitalize">
                          <DeviceIcon device={s.device} />
                          {s.device || "other"}
                        </span>
                      </td>
                      <td className="px-6 py-3 text-sm text-slate-700">
                        {s.city || s.country ? (
                          [s.city, s.country].filter(Boolean).join(", ")
                        ) : (
                          <span className="text-slate-400">Unknown</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="mt-12 border-t border-slate-200 pt-6 text-center">
          <Link to="/" className="text-xs text-slate-500 hover:text-slate-700">
            Powered by <span className="font-black text-slate-700">Qdurator</span> — dynamic QR campaigns for modern businesses
          </Link>
        </div>
      </main>
    </div>
  );
}